import React, { useState, useEffect, useCallback, useRef } from "react";
import "./citiesOverview.css";
import { getInventoryCities, branch_id } from "../api/api";
import { useGlobalFilter, PageBanner } from "../components/GlobalHeader";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip
} from "recharts";

function useAnimatedNumber(target, duration = 1400) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);
  useEffect(() => {
    if (target === 0) { setDisplay(0); return; }
    fromRef.current = display;
    startRef.current = null;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(fromRef.current + (target - fromRef.current) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return display;
}

const DEPTH = 12;

function Bar3DShape({ x, y, width, height, fill, fillDark, fillTop }) {
  if (!height || height <= 0) return null;
  const x2 = x + width, y2 = y, y1 = y + height;
  const front = `M${x},${y1} L${x2},${y1} L${x2},${y2} L${x},${y2} Z`;
  const top   = `M${x},${y2} L${x2},${y2} L${x2+DEPTH},${y2-DEPTH} L${x+DEPTH},${y2-DEPTH} Z`;
  const side  = `M${x2},${y1} L${x2},${y2} L${x2+DEPTH},${y2-DEPTH} L${x2+DEPTH},${y1-DEPTH} Z`;
  const shimmerX1 = x + width * 0.18, shimmerX2 = x + width * 0.38;
  const shimmerPath = `M${shimmerX1},${y1} L${shimmerX2},${y1} L${shimmerX2},${y2} L${shimmerX1},${y2} Z`;
  const topReflect = `M${x+2},${y2} L${x2-2},${y2} L${x2+DEPTH-2},${y2-DEPTH} L${x+DEPTH+2},${y2-DEPTH} Z`;
  return (
    <g style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.22)) drop-shadow(0 2px 4px rgba(0,0,0,0.14))" }}>
      <path d={front} fill={fill} />
      <path d={shimmerPath} fill="rgba(255,255,255,0.18)" />
      <line x1={x} y1={y2} x2={x2} y2={y2} stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
      <path d={top} fill={fillTop} />
      <path d={topReflect} fill="rgba(255,255,255,0.22)" />
      <path d={side} fill={fillDark} />
      <line x1={x2} y1={y2} x2={x2+DEPTH} y2={y2-DEPTH} stroke="rgba(255,255,255,0.30)" strokeWidth="1" />
    </g>
  );
}

function makeGradIds(dir) { return { allotted: `gradAllotted_${dir}`, active: `gradActive_${dir}` }; }

function AllottedBar({ gradId, ...props }) {
  return <Bar3DShape {...props} fill={`url(#${gradId || "gradAllotted_North"})`} fillDark="#9a5200" fillTop="#ffcf80" />;
}
function ActiveBar({ gradId, ...props }) {
  return <Bar3DShape {...props} fill={`url(#${gradId || "gradActive_North"})`} fillDark="#136060" fillTop="#a0f0f0" />;
}

function ChartDefs({ dir }) {
  const ids = makeGradIds(dir);
  return (
    <defs>
      <linearGradient id={ids.allotted} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#FFB347" />
        <stop offset="50%"  stopColor="#F7941D" />
        <stop offset="100%" stopColor="#e07a00" />
      </linearGradient>
      <linearGradient id={ids.active} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#72DADA" />
        <stop offset="50%"  stopColor="#47BDBD" />
        <stop offset="100%" stopColor="#259090" />
      </linearGradient>
    </defs>
  );
}

function formatY(v) {
  if (v === 0) return "0";
  if (v >= 100000) return `${parseFloat((v / 100000).toFixed(1))}L`;
  if (v >= 1000)   return `${parseFloat((v / 1000).toFixed(1))}k`;
  return String(v);
}

function CustomXTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text transform="rotate(-38)" textAnchor="end" dy={2}
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "18px", fontWeight: "900", fill: "#1e3a5f", letterSpacing: "0.01em" }}>
        {payload.value}
      </text>
    </g>
  );
}

function CustomYTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" dy={5}
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "18px", fontWeight: "900", fill: "#1e3a5f", letterSpacing: "0.01em" }}>
        {formatY(payload.value)}
      </text>
    </g>
  );
}

function CustomTooltip({ active, payload, label, coordinate, viewBox }) {
  if (!active || !payload?.length) return null;
  const allotted  = payload.find((p) => p.dataKey === "allotted")?.value ?? 0;
  const activeVal = payload.find((p) => p.dataKey === "active")?.value   ?? 0;
  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const TW = 220, TH = 148;
  const cx = coordinate?.x ?? 0;
  const cy = coordinate?.y ?? 0;
  const vw = viewBox?.width ?? 500;
  const vh = viewBox?.height ?? 400;
  const vl = viewBox?.left ?? 0;
  const vt = viewBox?.top ?? 0;

  let left = cx + 16;
  if (left + TW > vl + vw) left = cx - TW - 16;
  if (left < vl) left = vl + 4;

  let top = cy - 10;
  if (top + TH > vt + vh) top = vt + vh - TH - 4;
  if (top < vt) top = vt + 4;

  return (
    <div className="chart-tooltip" style={{ position: "absolute", left, top, pointerEvents: "none" }}>
      <div className="ctt-shimmer" />
      <div className="ctt-city">{label}</div>
      <div className="ctt-divider" />
      <div className="ctt-row">
        <span className="ctt-dot" style={{ background: "linear-gradient(135deg,#FFB347,#F7941D)" }} />
        <span className="ctt-label">Allotted</span>
        <strong className="ctt-value">{fmt(allotted)}</strong>
      </div>
      <div className="ctt-row">
        <span className="ctt-dot" style={{ background: "linear-gradient(135deg,#72DADA,#47BDBD)" }} />
        <span className="ctt-label">Active</span>
        <strong className="ctt-value">{fmt(activeVal)}</strong>
      </div>
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="skeleton-chart-wrap">
      <div className="skeleton-bars">
        {[70, 30, 90, 50, 40, 65, 20, 55].map((h, i) => (
          <div key={i} className="skeleton-bar-col">
            <div className="skeleton-bar-item" style={{ height: `${h}%`, animationDelay: `${i*0.08}s` }} />
            <div className="skeleton-bar-item sk-active" style={{ height: `${h*0.65}%`, animationDelay: `${i*0.08+0.1}s` }} />
          </div>
        ))}
      </div>
      <div className="skeleton-xlabel-row">
        {[80,65,90,70,55,75,45,60].map((w,i)=>(
          <div key={i} className="skeleton-xlabel-item" style={{ width:`${w}%`, animationDelay:`${i*0.06}s` }} />
        ))}
      </div>
    </div>
  );
}

/* ── Zone Summary Card ───────────────────────────────────────────────────── */
function SummaryCard({ title, emoji, accent, glow, allotted, active, loading }) {
  const animAllotted = useAnimatedNumber(loading ? 0 : allotted);
  const animActive   = useAnimatedNumber(loading ? 0 : active);
  const fmt = (n) => n.toLocaleString("en-IN");
  return (
    <div className="summary-card">
      <div className="summary-card-top-bar" style={{ background: accent }} />
      <div className="summary-card-shine" />
      <div className="summary-card-glow" style={{ background: `radial-gradient(circle, ${glow} 0%, transparent 70%)` }} />
      <div className="summary-card-label">
        <span className="summary-card-emoji">{emoji}</span>{title}
      </div>
      {loading ? (
        <div className="summary-skeleton-wrap">
          <div className="summary-skeleton summary-skeleton-big" />
          <div className="summary-skeleton summary-skeleton-sm" />
        </div>
      ) : (
        <>
          <div className="summary-card-main" style={{ color: accent }}>{fmt(animActive)}</div>
          <div className="summary-card-divider" style={{ background: accent }} />
          <div className="summary-card-sub-row">
            <div className="summary-card-sub">
              <span className="summary-sub-label">ALLOTTED</span>
              <span className="summary-sub-val">{fmt(animAllotted)}</span>
            </div>
            <div className="summary-card-sub">
              <span className="summary-sub-label">ACTIVE</span>
              <span className="summary-sub-val">{fmt(animActive)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SCROLL_THRESHOLD = 15;

function CityBarChart({ cities, loading, error, direction, expanded = false }) {
  if (loading) return <SkeletonChart />;
  if (error)   return <div className="chart-empty chart-error">⚠️ {error}</div>;
  if (!cities?.length) return <div className="chart-empty">No data available</div>;

  const needsScroll = expanded ? true : cities.length > SCROLL_THRESHOLD;
  const chartHeight = expanded ? 500 : 350;
  const chartWidth = expanded
    ? Math.max(cities.length * 100 + 100, 900)
    : needsScroll
      ? Math.max(cities.length * 75 + 80, 900)
      : "100%";

  return (
    <div className={expanded ? "recharts-wrap recharts-wrap-expanded" : "recharts-wrap"}>
      <div className="rc-legend">
        <div className="legend-pill"><span className="legend-swatch swatch-allotted" />Allotted</div>
        <div className="legend-pill"><span className="legend-swatch swatch-active" />Active</div>
      </div>
      <div className={needsScroll ? "chart-scroll-outer" : "chart-scroll-none"}>
        <div style={{ width: chartWidth, minWidth: chartWidth, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={cities} margin={{ top: 28, right: 36, left: 14, bottom: 72 }} barCategoryGap="28%" barGap={5}>
              <ChartDefs dir={direction} />
              <CartesianGrid vertical={false} stroke="rgba(30,58,95,0.08)" strokeDasharray="5 4" />
              <XAxis dataKey="name" tick={<CustomXTick />} tickLine={false}
                axisLine={{ stroke: "#c8d8ea", strokeWidth: 1.5 }} interval={0} height={72} />
              <YAxis tick={<CustomYTick />} tickLine={false} axisLine={false} width={56} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(99,102,241,0.06)", rx: 6 }}
                isAnimationActive={false} wrapperStyle={{ overflow: "visible", zIndex: 999 }} />
              <Bar dataKey="allotted" name="Allotted"
                shape={<AllottedBar gradId={makeGradIds(direction).allotted} />}
                isAnimationActive animationDuration={1000} animationEasing="ease-out" maxBarSize={expanded ? 56 : 40} />
              <Bar dataKey="active" name="Active"
                shape={<ActiveBar gradId={makeGradIds(direction).active} />}
                isAnimationActive animationDuration={1000} animationBegin={200} animationEasing="ease-out" maxBarSize={expanded ? 56 : 40} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ── Expanded Overlay ──────────────────────────────────────────────────────── */
function ExpandedCardOverlay({ card, regionData, onClose, onRetry }) {
  const { cities, loading, error } = regionData;

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="expand-overlay" onClick={onClose}>
      <div className="expand-modal" onClick={(e) => e.stopPropagation()}>
        <div className="expand-modal-top-bar" style={{ background: card.accent }} />
        <div className="expand-modal-header">
          <div className="expand-modal-title-group">
            <div className="city-direction-tag" style={{ color: card.accent }}>{card.direction}</div>
            <div className="city-card-title" style={{ fontSize: "22px" }}>
              <span className="city-card-emoji">{card.emoji}</span>{card.title}
            </div>
          </div>
          <button className="expand-close-btn" onClick={onClose} title="Close (Esc)">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="city-card-divider" style={{ background: card.accent, margin: "0 24px 16px" }} />
        {error && !loading && (
          <div className="error-row" style={{ margin: "0 24px 12px" }}>
            <span className="error-text">⚠️ {error}</span>
            <button className="retry-btn" onClick={onRetry}>Retry</button>
          </div>
        )}
        <div className="expand-modal-chart">
          <CityBarChart cities={cities} loading={loading} error={error} direction={card.direction} expanded={true} />
        </div>
      </div>
    </div>
  );
}

/* ── Reusable City Card (with chart) ─────────────────────────────────────── */
function CityCard({ card, regionData, onExpand, onRetry }) {
  const { cities, loading, error } = regionData;
  return (
    <div className="city-card">
      <div className="city-card-top-bar" style={{ background: card.accent }} />
      <div className="city-card-shine" />
      <div className="city-card-body">
        <div className="city-card-glow" style={{ background: `radial-gradient(circle, ${card.glow} 0%, transparent 70%)` }} />
        <div className="city-direction-tag" style={{ color: card.accent }}>{card.direction}</div>
        <div className="city-card-title-row">
          <div className="city-card-title">
            <span className="city-card-emoji">{card.emoji}</span>{card.title}
          </div>
          <button
            className="expand-btn"
            style={{ "--accent": card.accent }}
            onClick={onExpand}
            title="Expand to full screen"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 5V1H5M9 1H13V5M13 9V13H9M5 13H1V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Expand
          </button>
        </div>
        <div className="city-card-divider" style={{ background: card.accent }} />
        {error && !loading && (
          <div className="error-row">
            <span className="error-text">⚠️ {error}</span>
            <button className="retry-btn" onClick={onRetry}>Retry</button>
          </div>
        )}
        <CityBarChart cities={cities} loading={loading} error={error} direction={card.direction} />
      </div>
    </div>
  );
}

/* ── Chennai Solo Card — chart only, same as other zone cards ───────────── */
function ChennaiSoloCard({ card, regionData, onExpand, onRetry }) {
  const { cities, loading, error } = regionData;

  return (
    <div className="city-card chennai-solo-card">
      <div className="city-card-top-bar" style={{ background: card.accent }} />
      <div className="city-card-shine" />
      <div className="city-card-body">
        <div className="city-card-glow" style={{ background: `radial-gradient(circle, ${card.glow} 0%, transparent 70%)` }} />
        <div className="city-direction-tag" style={{ color: card.accent }}>{card.direction}</div>
        <div className="city-card-title-row">
          <div className="city-card-title">
            <span className="city-card-emoji">{card.emoji}</span>{card.title}
          </div>
          <button
            className="expand-btn"
            style={{ "--accent": card.accent }}
            onClick={onExpand}
            title="Expand to full screen"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 5V1H5M9 1H13V5M13 9V13H9M5 13H1V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Expand
          </button>
        </div>
        <div className="city-card-divider" style={{ background: card.accent }} />
        {error && !loading && (
          <div className="error-row">
            <span className="error-text">⚠️ {error}</span>
            <button className="retry-btn" onClick={onRetry}>Retry</button>
          </div>
        )}
        <CityBarChart
          cities={cities}
          loading={loading}
          error={error}
          direction={card.direction}
        />
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */
export default function CitiesOverview() {
  const { cas } = useGlobalFilter();
  const [expandedDir, setExpandedDir] = useState(null);

  const ALL_DIRECTIONS = ["North", "South", "East", "West", "Chennai"];

  const [regionState, setRegionState] = useState({
    North:   { cities: [], summary: { allotted: 0, active: 0 }, loading: true, error: "" },
    South:   { cities: [], summary: { allotted: 0, active: 0 }, loading: true, error: "" },
    East:    { cities: [], summary: { allotted: 0, active: 0 }, loading: true, error: "" },
    West:    { cities: [], summary: { allotted: 0, active: 0 }, loading: true, error: "" },
    Chennai: { cities: [], summary: { allotted: 0, active: 0 }, loading: true, error: "" },
  });

  const fetchRegion = useCallback(async (direction) => {
    setRegionState((prev) => ({
      ...prev,
      [direction]: { ...prev[direction], loading: true, error: "" },
    }));
    try {
      const res = await getInventoryCities(direction.toLowerCase(), cas, branch_id);
      const summary = {
        allotted: Number(res.data?.summary?.alloted  ?? 0),
        active:   Number(res.data?.summary?.assigned ?? 0),
      };
      const cities = (res.data?.cities ?? [])
        .map((c) => ({
          city_id: c.city_id,
          name:     c.city,
          allotted: Number(c.alloted   ?? 0),
          active:   Number(c.assigned  ?? 0),
        }))
        .filter((c) => c.allotted > 0 || c.active > 0)
        .sort((a, b) => b.allotted - a.allotted);
      setRegionState((prev) => ({
        ...prev,
        [direction]: { cities, summary, loading: false, error: "" },
      }));
    } catch (e) {
      setRegionState((prev) => ({
        ...prev,
        [direction]: { ...prev[direction], loading: false, error: e.message || "Failed to load" },
      }));
    }
  }, [cas]);

  useEffect(() => {
    ALL_DIRECTIONS.forEach((dir) => fetchRegion(dir));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRegion]);

  const chennaiCard = {
    direction: "Chennai", title: "Chennai Zone", emoji: "🏙️", accent: "#ff6b6b", glow: "rgba(255,107,107,0.22)",
  };

  const gridCards = [
    { direction: "North", title: "North Zone", emoji: "🧭", accent: "#00d4ff", glow: "rgba(0,212,255,0.22)"   },
    { direction: "South", title: "South Zone", emoji: "🌅", accent: "#f59e0b", glow: "rgba(245,158,11,0.22)"  },
    { direction: "East",  title: "East Zone",  emoji: "🌄", accent: "#10b981", glow: "rgba(16,185,129,0.22)"  },
    { direction: "West",  title: "West Zone",  emoji: "🌇", accent: "#a78bfa", glow: "rgba(167,139,250,0.22)" },
  ];

  const allCards = [chennaiCard, ...gridCards];

  const expandedCard = expandedDir
    ? allCards.find((c) => c.direction === expandedDir)
    : null;

  return (
    <div className="container">

      <PageBanner
        icon="🗺️"
        title="Zone Overview"
        subtitle="Regional city network performance across Tamil Nadu"
        badge="5 Regions"
      />

      {/* ── Per-zone summary cards (5 cards) — Chennai first ── */}
      <div className="summary-cards-row summary-cards-row--zones">
        {allCards.map((card) => {
          const { summary, loading } = regionState[card.direction];
          return (
            <SummaryCard
              key={card.direction}
              title={card.title}
              emoji={card.emoji}
              accent={card.accent}
              glow={card.glow}
              allotted={summary.allotted}
              active={summary.active}
              loading={loading}
            />
          );
        })}
      </div>

      {/* ── Chennai Zone — full-width solo card (chart) ── */}
      <div className="cities-grid cities-grid--solo">
        <ChennaiSoloCard
          card={chennaiCard}
          regionData={regionState["Chennai"]}
          onExpand={() => setExpandedDir("Chennai")}
          onRetry={() => fetchRegion("Chennai")}
        />
      </div>

      {/* ── 2×2 Grid: North / South / East / West ── */}
      <div className="cities-grid">
        {gridCards.map((card) => (
          <CityCard
            key={card.direction}
            card={card}
            regionData={regionState[card.direction]}
            onExpand={() => setExpandedDir(card.direction)}
            onRetry={() => fetchRegion(card.direction)}
          />
        ))}
      </div>

      {/* ── Expanded Overlay ── */}
      {expandedCard && (
        <ExpandedCardOverlay
          card={expandedCard}
          regionData={regionState[expandedCard.direction]}
          onClose={() => setExpandedDir(null)}
          onRetry={() => fetchRegion(expandedCard.direction)}
        />
      )}

    </div>
  );
}
